const FAR_FUTURE = "2099-12-31";

/**
 * Normalise une date pour comparaison (YYYY-MM-DD). Fin de contrat/cycle vide = FAR_FUTURE.
 * @param {string|Date|null|undefined} d
 * @returns {string}
 */
function normalizeDate(d) {
    if (d == null || d === "") return FAR_FUTURE;
    try {
        const date = typeof d === "string" ? new Date(d) : d;
        return date.toISOString().slice(0, 10);
    } catch {
        return FAR_FUTURE;
    }
}

/**
 * Jour suivant (YYYY-MM-DD).
 * @param {string} s
 * @returns {string}
 */
function dateDayAfter(s) {
    const d = new Date(s + "T12:00:00Z");
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
}

/**
 * Jour précédent (YYYY-MM-DD).
 * @param {string} s
 * @returns {string}
 */
function dateDayBefore(s) {
    const d = new Date(s + "T12:00:00Z");
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
}

/**
 * Intersection de deux périodes [aStart, aEnd] et [bStart, bEnd].
 * @returns {{ start_date: string, end_date: string } | null}
 */
function periodIntersect(aStart, aEnd, bStart, bEnd) {
    const start = aStart > bStart ? aStart : bStart;
    const end = aEnd < bEnd ? aEnd : bEnd;
    if (start > end) return null;
    return { start_date: start, end_date: end };
}

/**
 * Périodes dans [newStart, newEnd] qui ne sont pas dans [oldStart, oldEnd] ("jours en plus").
 * @returns {Array<{ start_date: string, end_date: string }>}
 */
function periodExtraOnly(oldStart, oldEnd, newStart, newEnd) {
    if (newEnd < oldStart || newStart > oldEnd) {
        return [{ start_date: newStart, end_date: newEnd }];
    }
    const out = [];
    if (newStart < oldStart) {
        out.push({ start_date: newStart, end_date: dateDayBefore(oldStart) });
    }
    if (newEnd > oldEnd) {
        out.push({ start_date: dateDayAfter(oldEnd), end_date: newEnd });
    }
    return out;
}

/**
 * Compare deux cycles (user_work_shift) : work_shift_id, start_date, end_date, start_week_or_day.
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
function cyclesEqual(a, b) {
    const aId = a?.work_shift_id ?? "";
    const bId = b?.work_shift_id ?? "";
    if (aId !== bId) return false;
    if (normalizeDate(a?.start_date) !== normalizeDate(b?.start_date)) return false;
    if (normalizeDate(a?.end_date) !== normalizeDate(b?.end_date)) return false;
    const aStartWoD = a?.start_week_or_day ?? "";
    const bStartWoD = b?.start_week_or_day ?? "";
    if (String(aStartWoD) !== String(bStartWoD)) return false;
    return true;
}

/**
 * Compare deux cycles uniquement sur structure : work_shift_id et start_week_or_day (pas les dates).
 */
function cyclesStructureEqual(a, b) {
    const aId = a?.work_shift_id ?? "";
    const bId = b?.work_shift_id ?? "";
    if (aId !== bId) return false;
    if (String(a?.start_week_or_day ?? "") !== String(b?.start_week_or_day ?? "")) return false;
    return true;
}

/**
 * Compare les tableaux user_work_shifts de deux contrats (même nombre, même contenu par cycle).
 * @param {Array} oldCycles
 * @param {Array} newCycles
 * @returns {boolean}
 */
function userWorkShiftsEqual(oldCycles, newCycles) {
    const oldList = oldCycles ?? [];
    const newList = newCycles ?? [];
    if (oldList.length !== newList.length) return false;
    const oldSorted = [...oldList].sort((x, y) => (normalizeDate(x?.start_date) || "").localeCompare(normalizeDate(y?.start_date) || ""));
    const newSorted = [...newList].sort((x, y) => (normalizeDate(x?.start_date) || "").localeCompare(normalizeDate(y?.start_date) || ""));
    for (let i = 0; i < oldSorted.length; i++) {
        if (!cyclesEqual(oldSorted[i], newSorted[i])) return false;
    }
    return true;
}

/**
 * Indique si les cycles ont la même structure (même nombre, même work_shift_id et start_week_or_day par cycle), les dates pouvant différer.
 */
function userWorkShiftsStructureEqual(oldCycles, newCycles) {
    const oldList = oldCycles ?? [];
    const newList = newCycles ?? [];
    if (oldList.length !== newList.length) return false;
    const oldSorted = [...oldList].sort((x, y) => (normalizeDate(x?.start_date) || "").localeCompare(normalizeDate(y?.start_date) || ""));
    const newSorted = [...newList].sort((x, y) => (normalizeDate(x?.start_date) || "").localeCompare(normalizeDate(y?.start_date) || ""));
    for (let i = 0; i < oldSorted.length; i++) {
        if (!cyclesStructureEqual(oldSorted[i], newSorted[i])) return false;
    }
    return true;
}

/**
 * Compare les dates de contrat (start_date, end_date).
 * @param {object} oldC
 * @param {object} newC
 * @returns {boolean}
 */
function contractDatesEqual(oldC, newC) {
    return (
        normalizeDate(oldC?.start_date) === normalizeDate(newC?.start_date) &&
        normalizeDate(oldC?.end_date) === normalizeDate(newC?.end_date)
    );
}

/**
 * Compare deux contrats (même id, mêmes dates, mêmes cycles).
 * @param {object} oldC
 * @param {object} newC
 * @returns {boolean}
 */
function contractEqual(oldC, newC) {
    if ((oldC?.id ?? "") !== (newC?.id ?? "")) return false;
    if (!contractDatesEqual(oldC, newC)) return false;
    if (!userWorkShiftsEqual(oldC?.user_work_shifts, newC?.user_work_shifts)) return false;
    return true;
}

/**
 * Calcule les périodes impactées pour un contrat modifié :
 * - si la structure des cycles a changé : intersection (cycle, contrat) pour chaque cycle concerné ;
 * - si seules les dates (contrat ou cycles) ont changé : uniquement les jours en plus (new \ old).
 * @param {object} oldC
 * @param {object} newC
 * @returns {Array<{ start_date: string, end_date: string }>}
 */
function changedPeriodsForModifiedContract(oldC, newC) {
    const newStart = normalizeDate(newC?.start_date);
    const newEnd = normalizeDate(newC?.end_date);
    const oldStart = normalizeDate(oldC?.start_date);
    const oldEnd = normalizeDate(oldC?.end_date);
    const newCycles = newC?.user_work_shifts ?? [];
    const oldCycles = oldC?.user_work_shifts ?? [];
    const periods = [];

    const cycleStructureChanged = !userWorkShiftsStructureEqual(oldCycles, newCycles);

    if (cycleStructureChanged) {
        for (const cycle of newCycles) {
            const cStart = normalizeDate(cycle?.start_date);
            const cEnd = normalizeDate(cycle?.end_date);
            const inter = periodIntersect(cStart, cEnd, newStart, newEnd);
            if (inter) periods.push(inter);
        }
    } else {
        if (!contractDatesEqual(oldC, newC)) {
            periods.push(...periodExtraOnly(oldStart, oldEnd, newStart, newEnd));
        }
        const oldSorted = [...oldCycles].sort((x, y) => normalizeDate(x?.start_date).localeCompare(normalizeDate(y?.start_date)));
        const newSorted = [...newCycles].sort((x, y) => normalizeDate(x?.start_date).localeCompare(normalizeDate(y?.start_date)));
        for (let i = 0; i < newSorted.length; i++) {
            const oc = oldSorted[i];
            const nc = newSorted[i];
            if (!cyclesStructureEqual(oc, nc)) continue;
            const oStart = normalizeDate(oc?.start_date);
            const oEnd = normalizeDate(oc?.end_date);
            const nStart = normalizeDate(nc?.start_date);
            const nEnd = normalizeDate(nc?.end_date);
            if (oStart === nStart && oEnd === nEnd) continue;
            const extra = periodExtraOnly(oStart, oEnd, nStart, nEnd);
            for (const p of extra) {
                const inter = periodIntersect(p.start_date, p.end_date, newStart, newEnd);
                if (inter) periods.push(inter);
            }
        }
    }

    return periods;
}

/**
 * Compare oldContracts et newContracts et retourne les différences ainsi que les périodes impactées dans newContracts.
 * Pour les contrats ajoutés : période complète du contrat.
 * Pour les contrats modifiés : intersection cycle/contrat si le cycle est modifié, sinon uniquement les jours en plus.
 * @param {Array} oldContracts
 * @param {Array} newContracts
 * @returns {{ hasDifferences: boolean, added: Array, removed: Array, modified: Array<{old: object, new: object}>, changedPeriodsInNewContracts: Array<{start_date: string, end_date: string}> }}
 */
export function contractsDiff(oldContracts, newContracts) {
    const oldList = oldContracts ?? [];
    const newList = newContracts ?? [];
    const oldById = new Map(oldList.map((c) => [c?.id, c]));
    const newById = new Map(newList.map((c) => [c?.id, c]));

    const added = [];
    const removed = [];
    const modified = [];

    for (const newC of newList) {
        const id = newC?.id;
        const oldC = oldById.get(id);
        if (!oldC) {
            added.push(newC);
            continue;
        }
        if (!contractEqual(oldC, newC)) {
            modified.push({ old: oldC, new: newC });
        }
    }

    for (const oldC of oldList) {
        if (!newById.has(oldC?.id)) {
            removed.push(oldC);
        }
    }

    const changedPeriodsInNewContracts = [
        ...added.map((c) => ({
            start_date: normalizeDate(c?.start_date),
            end_date: normalizeDate(c?.end_date),
        })),
        ...modified.flatMap(({ old: oldC, new: newC }) => changedPeriodsForModifiedContract(oldC, newC)),
    ];

    return {
        hasDifferences: added.length > 0 || removed.length > 0 || modified.length > 0,
        added,
        removed,
        modified,
        changedPeriodsInNewContracts,
    };
}
